// Homepage — Fieldbook language. Self-contained (renders its own FieldHeader/
// FieldFooter + .ffly--public), routed standalone at "/". Prerendered at deploy.
// Content-led, not a reskin: hero → "show the value" before/after → data types → access.
import { PageMeta } from "@/components/PageMeta";
import { JsonLd } from "@/components/JsonLd";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "./home.css";

const DATA_TYPES = [
  { nm: "NPI", desc: "Providers, enriched with NUCC specialty names", c: "#1f6fb2", href: "/docs/value-apis/npi" },
  { nm: "NDC", desc: "Drugs with brand, generic & manufacturer", c: "#f46f63", href: "/docs/value-apis/ndc" },
  { nm: "ICD-10", desc: "Diagnoses with human-readable descriptions", c: "#38afa7", href: "/docs/value-apis/icd10" },
  { nm: "RxNorm", desc: "Clinical drugs & ingredient mappings", c: "#3f8a58", href: "/docs/value-apis/rxnorm" },
  { nm: "LOINC", desc: "Lab tests with long common names", c: "#d6a224", href: "/docs/value-apis/loinc" },
  { nm: "SNOMED", desc: "Clinical terms with display names", c: "#1f6fb2", href: "/docs/value-apis/snomed" },
  { nm: "HCPCS", desc: "Procedures & coverage detail", c: "#f46f63", href: "/docs/value-apis/hcpcs" },
  { nm: "+15 more", desc: "CVX, MVX, FDA Labels, HCC, dm+d, MS-DRG…", c: "#15324e", href: "/docs/value-apis/overview" },
];

export default function Home() {
  return (
    <div className="ffly ffly--public home-page">
      <PageMeta
        title="Healthcare terminology, enriched — NPI, NDC, ICD-10 & more"
        description="Raw FHIR gives you a code; FHIRfly gives you the meaning. Provider, drug, lab, and specialty names across 21+ healthcare data types — via API, SDK, or MCP. Free tools, no sign-up."
        canonical={canonicalUrl("/")}
      />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "FHIRfly",
        url: "https://fhirfly.io",
        logo: "https://fhirfly.io/logo.png",
        description: "RESTful APIs and TypeScript SDKs for 21+ healthcare data types. Enrich FHIR resources with display names, provider details, and drug profiles.",
        sameAs: [
          "https://x.com/fhirfly_io",
          "https://www.linkedin.com/company/fhirfly",
          "https://github.com/FHIRfly-io",
        ],
      }} />

      <FieldHeader />

      <div className="wrap">
        <section className="hero">
          <div>
            <div className="plate">Healthcare terminology, enriched</div>
            <h1>Codes in.<br /><em>Meaning out.</em></h1>
            <p className="lede">
              Raw FHIR gives you a code. FHIRfly gives you the provider, the drug, the lab, the
              specialty — names humans read, sourced daily from official registries.
            </p>
            <div className="ctas">
              <a className="btn-primary" href="/tools/npi-lookup">Try a free tool</a>
              <a className="link" href="/tools/api-playground">Or run the API →</a>
            </div>
            <div className="reassure">Free tools, no sign-up · Developer API with a free tier</div>
          </div>
          <figure className="figure">
            <div className="frame">
              <img src="/hero/hero-firefly.png" alt="Riso botanical specimen illustration of a firefly" />
            </div>
            <figcaption className="cap">Specimen № 01 · FHIRfly</figcaption>
          </figure>
        </section>

        <div className="trust">
          <span className="lbl">Sourced daily from official registries</span>
          <span className="src">CMS / NPPES</span>
          <span className="src">FDA</span>
          <span className="src">NLM / RxNorm</span>
          <span className="src">Regenstrief / LOINC</span>
          <span className="src">CDC</span>
        </div>

        {/* show the value, don't assert it */}
        <section className="compare">
          <h2>What “enriched” actually means</h2>
          <p className="sub">The same code, before and after. One call adds the meaning your app was missing.</p>
          <div className="panes">
            <div className="cmp raw">
              <div className="cap">Raw FHIR — a code, no meaning</div>
              <pre>{`{
  `}<span className="key">"system"</span>{`: `}<span className="str">"…/icd-10-cm"</span>{`,
  `}<span className="key">"code"</span>{`:   `}<span className="str">"E11.9"</span>{`
}`}</pre>
            </div>
            <div className="cmp enriched">
              <div className="cap">With FHIRfly — meaning attached</div>
              <pre>{`{
  `}<span className="key">"code"</span>{`:     `}<span className="str">"E11.9"</span>{`,
  `}<span className="add">"display":  "Type 2 diabetes mellitus
              without complications"</span>{`,
  `}<span className="add">"chapter":  "Endocrine & metabolic"</span>{`,
  `}<span className="add">"billable": true</span>{`
}`}</pre>
            </div>
          </div>
        </section>

        <section className="section">
          <h2>Every code, enriched</h2>
          <p className="sub">21+ healthcare data types — each returning names, not just identifiers.</p>
          <div className="grid">
            {DATA_TYPES.map((d) => (
              <a className="tile" key={d.nm} href={d.href}>
                <div className="nm"><span className="dot" style={{ background: d.c }} />{d.nm}</div>
                <div className="desc">{d.desc}</div>
              </a>
            ))}
          </div>

          <div className="access">
            <a className="acard" href="/tools/api-playground">
              <div className="k">For developers</div>
              <h3>REST API</h3>
              <p>One key, every data type. Try any endpoint live in the playground before you sign up.</p>
            </a>
            <a className="acard" href="https://www.npmjs.com/package/@fhirfly-io/terminology" target="_blank" rel="noopener noreferrer">
              <div className="k">Drop-in</div>
              <h3>TypeScript SDK</h3>
              <p>@fhirfly-io/terminology — typed clients for all 21+ data types.</p>
            </a>
            <a className="acard" href="/docs/mcp/overview">
              <div className="k">For agents</div>
              <h3>MCP Server</h3>
              <p>54+ tools for Claude &amp; other agents — healthcare terminology on tap.</p>
            </a>
          </div>
        </section>
      </div>

      <FieldFooter variant="full" />
    </div>
  );
}
